/*
 * What `POST /public/cart/validate` says about the cart, in the shape the UI
 * needs (docs/PHASE2.md section 5):
 *
 *   line.issues  UNAVAILABLE        the variant is gone (or never existed)
 *                OUT_OF_STOCK       nothing left to sell
 *                QUANTITY_EXCEEDS_STOCK  fewer left than the cart asks for
 *                PRICE_CHANGED      the price differs from the one the cart showed
 *   issues       { code, message, field? }: ITEMS_UNAVAILABLE, COUPON_*
 *                (field 'couponCode'), SHIPPING_QUOTE_INVALID|EXPIRED|STALE
 *                (field 'shipping')
 *
 * line.shippingRestriction 'instant_only' (docs/PHASE3.md 6.2): the product
 *              travels only with an instant courier or is collected at the shop.
 *
 * The first three stop checkout until the shopper removes or fixes the line;
 * a price change is only shown (the server's total is what gets charged).
 */
export const BLOCKING_LINE_ISSUES = ['UNAVAILABLE', 'OUT_OF_STOCK', 'QUANTITY_EXCEEDS_STOCK'];

export const isCouponIssue = (code) => typeof code === 'string' && code.startsWith('COUPON_');
export const isShippingIssue = (code) => typeof code === 'string' && code.startsWith('SHIPPING_QUOTE_');

/**
 * @param {object[]} cartLines the cart (snapshots: priceAmount is what the shopper saw)
 * @param {object} response the `value` of the validate call
 */
export function summarizeValidation(cartLines, response) {
  const res = response || {};
  const snapshot = new Map((cartLines || []).map((l) => [l.variantId, l]));
  const lines = {};
  const priceChanges = [];
  const blocking = [];
  const instantOnly = [];

  (res.lines || []).forEach((s) => {
    const issues = Array.isArray(s.issues) ? s.issues : [];
    const isBlocking = issues.some((i) => BLOCKING_LINE_ISSUES.includes(i));
    const seen = snapshot.get(s.variantId);
    const priceChange =
      issues.includes('PRICE_CHANGED') && seen && Number.isFinite(s.unitPriceAmount)
        ? { from: seen.priceAmount, to: s.unitPriceAmount }
        : null;
    lines[s.variantId] = {
      issues,
      blocking: isBlocking,
      available: s.available !== false,
      maxQuantity: Number.isFinite(s.maxQuantity) ? s.maxQuantity : null,
      unitPriceAmount: Number.isFinite(s.unitPriceAmount) ? s.unitPriceAmount : null,
      lineSubtotalAmount: Number.isFinite(s.lineSubtotalAmount) ? s.lineSubtotalAmount : null,
      priceChange,
      instantOnly: s.shippingRestriction === 'instant_only',
    };
    if (isBlocking) blocking.push(s.variantId);
    if (s.shippingRestriction === 'instant_only') instantOnly.push(s.variantId);
    if (priceChange) priceChanges.push({ variantId: s.variantId, ...priceChange });
  });

  const top = Array.isArray(res.issues) ? res.issues : [];
  const couponIssue = top.find((i) => isCouponIssue(i.code)) || null;
  const shippingIssue = top.find((i) => isShippingIssue(i.code)) || null;

  return {
    lines,
    blockingVariantIds: blocking,
    hasBlocking: blocking.length > 0,
    priceChanges,
    instantOnlyVariantIds: instantOnly,
    coupon: res.coupon || null,
    couponIssue: couponIssue && { code: couponIssue.code },
    shippingIssue: shippingIssue && { code: shippingIssue.code },
    pricing: res.pricing || null,
    shipping: res.shipping || null,
    serverLines: res.lines || [],
    // Methods with the fee each carries for the buyer (docs/MIDTRANS.md 11.3), for this total.
    paymentOptions: res.paymentOptions || null,
  };
}

/**
 * STOCK_INSUFFICIENT (409) from checkout: `details.lines` is
 * `[{ variantId, requested, available }]`. Returns per-variant marks in the
 * same shape as the validation lines, so the cart can show them at once.
 */
export function stockIssueMarks(details) {
  const marks = {};
  const list = details && Array.isArray(details.lines) ? details.lines : [];
  list.forEach((l) => {
    if (!l || typeof l.variantId !== 'string') return;
    const available = Math.max(0, Number(l.available) || 0);
    marks[l.variantId] = {
      issues: [available === 0 ? 'OUT_OF_STOCK' : 'QUANTITY_EXCEEDS_STOCK'],
      blocking: true,
      available: available > 0,
      maxQuantity: available,
      requested: Number(l.requested) || null,
    };
  });
  return marks;
}

export const initialValidation = Object.freeze({ status: 'idle', data: null, error: null, key: null });

/**
 * Validation state:
 *   idle        nothing asked yet (or validation is off)
 *   loading     first answer pending
 *   refreshing  an answer is shown, a newer one is pending
 *   ready       an answer arrived; `key` says what it answered (compare with
 *               validationKey() of the cart as it is now to know it is current)
 *   error       the call failed; `data` keeps the last good answer, if any
 */
export function validationReducer(state, action) {
  switch (action.type) {
    case 'start':
      return { ...state, status: state.data ? 'refreshing' : 'loading', error: null };
    case 'success':
      return { status: 'ready', data: action.summary, error: null, key: action.key ?? null };
    case 'failure':
      return { status: 'error', data: state.data, error: action.error };
    case 'reset':
      return initialValidation;
    default:
      return state;
  }
}

/** Stable identity of what is being validated; a change means "ask again". */
export function validationKey({ lines, couponCode, shipping }) {
  const items = lines.map((l) => `${l.variantId}:${l.quantity}:${l.priceAmount}`).join('|');
  return `${items}#${couponCode || ''}#${shipping ? `${shipping.optionId}:${shipping.quoteToken}` : ''}`;
}
